/**
 * ChartTemplateFormModal — panel ITE „Wzorce planu kont" (create/edit meta wzorca).
 *
 * Pola: name/description/isActive. Bez `code` i bez cyklu statusów
 * draft/published/withdrawn (w przeciwieństwie do `GlobalDictionaryFormModal`) —
 * `isActive=false` wystarcza do wycofania wzorca z pickera adopcji tenanta bez
 * hard-delete (mirror decyzji z ADR-0081).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import type { ChartTemplateFormValues } from '@/lib/chart-of-accounts-templates/api';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: ChartTemplateFormValues;
  saving?: boolean;
  serverErrors?: Record<string, string>;
  onSubmit: (values: ChartTemplateFormValues) => void;
  onClose: () => void;
}

export function ChartTemplateFormModal({
  open,
  mode,
  initialValues,
  saving = false,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) setErrors((prev) => ({ ...prev, ...serverErrors }));
  }, [serverErrors]);

  function set<K extends keyof ChartTemplateFormValues>(key: K, value: ChartTemplateFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.name.trim()) {
      next.name = t('admin.chartTemplates.form.errors.nameRequired');
    } else if (values.name.trim().length > 200) {
      next.name = t('admin.chartTemplates.form.errors.nameLength');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({ ...values, name: values.name.trim(), description: values.description.trim() });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        mode === 'create'
          ? 'admin.chartTemplates.form.titleCreate'
          : 'admin.chartTemplates.form.titleEdit',
      )}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="chart-template-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="chart-template-form">
        <FormField label={t('admin.chartTemplates.form.name')} required error={errors.name}>
          <Input
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            maxLength={200}
            data-testid="chart-template-form-name"
          />
        </FormField>

        <FormField label={t('admin.chartTemplates.form.description')}>
          <Textarea
            value={values.description}
            onChange={(e) => set('description', e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid="chart-template-form-description"
          />
        </FormField>

        <Checkbox
          label={t('admin.chartTemplates.form.isActive')}
          hint={t('admin.chartTemplates.form.isActiveHint')}
          checked={values.isActive}
          onChange={(e) => set('isActive', e.target.checked)}
          data-testid="chart-template-form-is-active"
        />
      </div>
    </Modal>
  );
}
